// Rapprochements (H4-09) : relevés importés (CSV reference;montant;date, canal, période), synthèse
// de chaque relevé (lignes, automatiques, manuelles, écarts, intégral), import d'un relevé par le
// financier (formulaire multipart).
import { Bandeau, Bouton, Dialogue, EtatVide, FichierChamp, GrilleChamps, Lien, PuceEtat, Selection, Tableau, DateChamp, type ColonneTableau } from '@cc/ui';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { requete } from '../../api/client';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { CODES_CANAL, LIBELLES_CANAL, francs, libelle, type ReleveDetail, type ReleveResume } from './donnees';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { INVALIDER_RECETTES, usePrestataires, useReleves } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function puceReleve(r: Pick<ReleveResume, 'integral' | 'ecarts'>) {
  return r.integral ? <PuceEtat ton="ok" libelle="Intégralement rapproché" /> : <PuceEtat ton="retard" libelle={`${r.ecarts} écart${r.ecarts > 1 ? 's' : ''}`} />;
}

export function ImportReleve({ ouvert, onFermer }: { ouvert: boolean; onFermer: () => void }) {
  const navigate = useNavigate();
  const prestataires = usePrestataires(ouvert);
  const [s, setS] = useState({ canal: 'simulateur', prestataire: '', debut: '', fin: '' });
  const [fichiers, setFichiers] = useState<File[]>([]);
  const action = useAction<undefined, ReleveDetail>({
    executer: () => {
      const f = new FormData();
      const fichier = fichiers[0];
      if (fichier) f.set('fichier', fichier);
      f.set('canal', s.canal);
      if (s.prestataire) f.set('prestataire', s.prestataire);
      f.set('periode_debut', s.debut);
      f.set('periode_fin', s.fin || s.debut);
      return requete<ReleveDetail>('/recettes/releves', { methode: 'POST', corps: f });
    },
    succes: (r) => (r.synthese.integral ? `Relevé importé : ${r.synthese.lignes} ligne(s), intégralement rapproché.` : `Relevé importé : ${r.synthese.lignes} ligne(s), ${r.synthese.ecarts} écart(s) à instruire.`),
    invalider: INVALIDER_RECETTES,
    apres: (r) => {
      onFermer();
      setFichiers([]);
      void navigate(`/recettes/rapprochements/${r.id}`);
    },
  });
  const champ = (cle: keyof typeof s) => (e: { target: { value: string } }) => setS((x) => ({ ...x, [cle]: e.target.value }));
  const complet = fichiers.length === 1 && /^\d{4}-\d{2}-\d{2}$/.test(s.debut) && (!s.fin || s.fin >= s.debut);
  return (
    <Dialogue
      ouvert={ouvert}
      titre="Importer un relevé"
      onFermer={onFermer}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton chargement={action.enCours} disabled={!complet} onClick={() => action.lancer(undefined)}>
            Importer et rapprocher
          </Bouton>
        </>
      }
    >
      <p>Fichier CSV avec l&apos;en-tête <span className={styles.mono}>reference;montant;date</span> (séparateur point-virgule ou virgule, montants en francs sans décimale, espaces admis). Chaque ligne est rapprochée d&apos;un paiement confirmé du canal par sa référence (serveur ou prestataire) et son montant ; les paiements confirmés de la période absents du relevé sont des écarts.</p>
      <GrilleChamps>
        <Selection id="releve-canal" libelle="Canal" value={s.canal} options={CODES_CANAL.map((c) => ({ valeur: c, libelle: LIBELLES_CANAL[c] ?? c }))} onChange={champ('canal')} />
        <Selection id="releve-prestataire" libelle="Prestataire" facultatif optionVide="Tous" value={s.prestataire} options={(prestataires.data ?? []).map((p) => ({ valeur: p.prestataire, libelle: p.prestataire }))} onChange={champ('prestataire')} />
        <DateChamp id="releve-debut" libelle="Début de la période" required value={s.debut} erreur={action.erreursChamps.find((x) => x.champ === 'periode_debut')?.message} onChange={champ('debut')} />
        <DateChamp id="releve-fin" libelle="Fin de la période" facultatif aide="Même jour que le début si vide" value={s.fin} erreur={action.erreursChamps.find((x) => x.champ === 'periode_fin')?.message} onChange={champ('fin')} />
      </GrilleChamps>
      <FichierChamp id="releve-fichier" libelle="Relevé CSV" fichiers={fichiers} onFichiers={setFichiers} accept=".csv,text/csv" tailleMax={10 * 1024 * 1024} required />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function PageRapprochements() {
  const droits = useDroitsRecettes();
  const liste = useReleves();
  const [importer, setImporter] = useState(false);
  const colonnes: ColonneTableau<ReleveResume>[] = [
    { id: 'periode', entete: 'Période', enTeteLigne: true, masquable: false, valeurTri: (r) => r.periode.debut ?? null, cellule: (r) => <Lien href={`/recettes/rapprochements/${r.id}`} className={commun.reference}>{r.periode.debut === r.periode.fin ? date(r.periode.debut) : `${date(r.periode.debut)} au ${date(r.periode.fin)}`}</Lien> },
    { id: 'canal', entete: 'Canal', valeurTri: (r) => r.canal, cellule: (r) => `${libelle(LIBELLES_CANAL, r.canal)}${r.prestataire ? `, ${r.prestataire}` : ''}` },
    { id: 'importe', entete: 'Importé le', valeurTri: (r) => r.importe_le ?? null, cellule: (r) => dateHeure(r.importe_le) },
    { id: 'lignes', entete: 'Lignes', numerique: true, cellule: (r) => `${r.lignes} (${r.auto} auto, ${r.manuel} manuelles)` },
    { id: 'total', entete: 'Total du relevé', numerique: true, cellule: (r) => <span className={styles.montant}>{francs(r.total_releve)}</span> },
    { id: 'rapproche', entete: 'Rapproché', numerique: true, cellule: (r) => <span className={styles.montant}>{francs(r.total_rapproche)}</span> },
    { id: 'etat', entete: 'État', valeurTri: (r) => r.ecarts, cellule: puceReleve },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Rapprochements"
        description="Relevés des prestataires et des banques rapprochés des paiements confirmés ; un écart ouvre une anomalie et se résout par un rapprochement manuel motivé."
        actions={droits.modifier ? <Bouton icone={Upload} onClick={() => setImporter(true)}>Importer un relevé</Bouton> : null}
      />
      <NavigationFinances />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} lot="L2-A" fonction="La liste des relevés">
        {(liste.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun relevé importé" icone={FileSpreadsheet}>
            <p>Importez un relevé CSV pour le rapprocher des paiements confirmés.</p>
          </EtatVide>
        ) : (
          <Tableau legende="Relevés" colonnes={colonnes} lignes={liste.data ?? []} cleLigne={(r) => r.id} />
        )}
      </EtatRequete>
      {importer ? <ImportReleve ouvert onFermer={() => setImporter(false)} /> : null}
    </>
  );
}
